import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Selbstbedienungsautomat“ (/selbstbedienungsautomat).
 * Eigener Winkel: SB-Verkauf OHNE die Nachteile von offenem Regal/Vertrauens-
 * kasse – kein Schwund, kein doppeltes Kassenbuch, kein Finanzamt-Ärger, jede
 * Zahlung digital nachweisbar. Referenz: Hofbäckerei Hömberg (Live + Video).
 * Blog: einen-selbstbedienungsschrank-eroeffnen.
 */
@Component({
  selector: 'app-selbstbedienungsautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './selbstbedienungsautomat-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class SelbstbedienungsautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'einen-selbstbedienungsschrank-eroeffnen';

  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/hofbaeckerei-hoemberg/U-MB-00035'
  );

  readonly videoThumb = 'https://i.ytimg.com/vi/BODwbOYEAgg/hqdefault.jpg';
  videoOpen = false;
  videoUrl?: SafeResourceUrl;

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@sb.faq1.q:Was ist der Unterschied zu einer Vertrauenskasse oder einem offenen Regal?`,
      a: $localize`:@@sb.faq1.a:Beim offenen SB-Regal mit Vertrauenskasse gibt es Schwund und Bargeld-Handling. Der Selbstbedienungsautomat gibt Ware erst nach Zahlung frei – kein Schwund, kein Bargeld, jede Zahlung digital dokumentiert.`,
    },
    {
      q: $localize`:@@sb.faq2.q:Wie wird bezahlt?`,
      a: $localize`:@@sb.faq2.a:Bargeldlos direkt am Automaten. Es gibt kein zweites Kassenbuch und keinen Ärger mit dem Finanzamt – jede Transaktion lässt sich digital nachweisen.`,
    },
    {
      q: $localize`:@@sb.faq3.q:Ist die Ware vor Diebstahl und Wetter geschützt?`,
      a: $localize`:@@sb.faq3.a:Ja. Die marktbox hat einzeln abschließbare Klappen und ist wetterfest – die Ware bleibt geschützt und trotzdem rund um die Uhr verfügbar.`,
    },
    {
      q: $localize`:@@sb.faq4.q:Selbstbedienungsautomat kaufen oder mieten?`,
      a: $localize`:@@sb.faq4.a:Beides: ab 6.800 € kaufen oder ab 300 € pro Monat mieten – je nach Modell und Zubehör.`,
    },
  ];

  openVideo(): void {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube-nocookie.com/embed/BODwbOYEAgg?autoplay=1&rel=0'
    );
    this.videoOpen = true;
  }

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@sb.metaTitle:Selbstbedienungsautomat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@sb.metaDesc:Selbstbedienungsautomat kaufen oder mieten: SB-Verkauf ohne Schwund – Ware erst nach Zahlung. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content: $localize`:@@sb.metaKeywords:Selbstbedienungsautomat, SB-Automat, Selbstbedienung ohne Vertrauenskasse, Verkaufsautomat Selbstbedienung, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('sb-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Selbstbedienungsautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('sb-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    this.injectJsonLd('sb-video-schema', {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'marktbox – Zu Gast bei der Hofbäckerei Hömberg',
      description:
        'Zu Besuch bei der Hofbäckerei Hömberg: Wie Selbstbedienung mit der marktbox funktioniert – ohne Schwund, ohne Bargeld-Handling, mit digitalem Zahlungsnachweis.',
      thumbnailUrl: 'https://i.ytimg.com/vi/BODwbOYEAgg/hqdefault.jpg',
      uploadDate: '2024-09-27',
      duration: 'PT4M4S',
      embedUrl: 'https://www.youtube-nocookie.com/embed/BODwbOYEAgg',
      contentUrl: 'https://www.youtube.com/watch?v=BODwbOYEAgg',
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/selbstbedienungsautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
